import { useState, useEffect } from 'react';
import { Clock, Calendar } from 'lucide-react';
import { cn } from '@/utils/cn';

interface CountdownTimerProps {
  endDate: Date;
  variant?: 'compact' | 'detailed' | 'hero';
  className?: string;
  onExpire?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer = ({ 
  endDate, 
  variant = 'detailed',
  className,
  onExpire 
}: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = endDate.getTime();
      const difference = target - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (!isExpired) {
          setIsExpired(true);
          onExpire?.();
        }
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate, isExpired, onExpire]);

  if (isExpired) {
    return (
      <div className={cn(
        'flex items-center gap-2 text-red-600',
        variant === 'compact' && 'text-sm',
        className
      )}>
        <Calendar className="h-4 w-4" aria-label="Calendar icon" />
        <span className="font-semibold">Offer Expired</span>
      </div>
    );
  }

  const getTimeUnit = (value: number, unit: string) => {
    const displayValue = value.toString().padStart(2, '0');
    
    switch (variant) {
      case 'compact':
        return (
          <span className="text-xs font-mono">
            {displayValue}{unit.charAt(0)}
          </span>
        );
      
      case 'hero':
        return (
          <div className="flex flex-col items-center p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold font-mono">
              {displayValue}
            </div>
            <div className="text-xs sm:text-sm font-medium uppercase tracking-wide">
              {unit}{value !== 1 ? 's' : ''}
            </div>
          </div>
        );
      
      default: // detailed
        return (
          <div className="flex flex-col items-center p-2 sm:p-3 bg-gradient-to-b from-green-500 to-emerald-600 text-white rounded-lg shadow-lg">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold font-mono">
              {displayValue}
            </div>
            <div className="text-xs sm:text-sm font-medium uppercase tracking-wide">
              {unit}{value !== 1 ? 's' : ''}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={cn(
      'flex items-center gap-2',
      variant === 'hero' && 'text-white',
      className
    )}>
      {variant !== 'compact' && (
        <div className="flex items-center gap-1 mr-2">
          <Clock className={cn(
            'animate-pulse',
            variant === 'hero' ? 'h-5 w-5' : 'h-4 w-4',
            variant === 'hero' ? 'text-white' : 'text-green-600'
          )} />
          <span className={cn(
            'font-semibold',
            variant === 'hero' ? 'text-white text-sm sm:text-base' : 'text-green-800 text-sm',
          )}>
            Ends in:
          </span>
        </div>
      )}
      
      <div className={cn(
        'flex items-center',
        variant === 'compact' ? 'gap-1' : 'gap-2 sm:gap-3'
      )}>
        {timeLeft.days > 0 && getTimeUnit(timeLeft.days, 'Day')}
        {getTimeUnit(timeLeft.hours, 'Hour')}
        {getTimeUnit(timeLeft.minutes, 'Min')}
        {variant !== 'compact' && getTimeUnit(timeLeft.seconds, 'Sec')}
        
        {variant === 'compact' && (
          <Clock className="h-3 w-3 text-green-600 animate-pulse ml-1" aria-label="Clock icon" />
        )}
      </div>
    </div>
  );
};

export default CountdownTimer;